import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Typography,
  MenuItem,
  Grid,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Download, Assessment } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import axios from '../../../resources/axios';
import { formatDate } from '../../../resources/functions';
import { selectCities } from '../../../redux/slices/citiesSlice';
import { selectAdmins } from '../../../redux/slices/adminsSlice';
import { selectAuth } from '../../../redux/slices/authSlice';

interface BonusStats {
  name: string;
  views: number;
  uniqueViews: number;
  activations: number;
  conversion?: string;
}

const CITY_SHORT_MAP: Record<string, string> = {
  '5ff99e7d863d42769b05c486': 'IF',
  '60d59e08bd31e836ae204610': 'VN',
  '60058dc791cd964825147a6a': 'ZT',
  '63f7b73cfcb1a10e71e62078': 'BT',
  '63fc94c0fcb1a10e71e620b7': 'KL',
  '66280975a8831e51b380e3cd': 'CHR',
  '66a2533d5f572298529446b8': 'VSK',
  '68721a559e0fad9ff9ad52a9': 'KAL'
};

const getCityShort = (cityId: string): string => {
  return CITY_SHORT_MAP[cityId] || cityId;
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D'];

const UsageAnalysisTab = () => {
  const theme = useTheme();
  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);

  const [selectedCityShort, setSelectedCityShort] = useState<string>('');
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [data, setData] = useState<BonusStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const availableCities = useMemo(() => {
    if (auth.info?.type === 'superAdmin') {
      return cities.items;
    }
    if (adminInfo.info?.cities) {
      return adminInfo.info.cities;
    }
    return [];
  }, [auth.info?.type, adminInfo.info?.cities, cities.items]);

  const currentCity = useMemo(() => {
    return availableCities.find(item => item._id === adminInfo.currentCity);
  }, [adminInfo.currentCity, availableCities]);

  useEffect(() => {
    if (currentCity && !selectedCityShort) {
      setSelectedCityShort(getCityShort(currentCity._id));
    } else if (!selectedCityShort && availableCities.length > 0) {
      setSelectedCityShort(getCityShort(availableCities[0]._id));
    }
  }, [currentCity, selectedCityShort, availableCities]);

  const handleFetchData = async () => {
    if (!selectedCityShort) {
      setError('Виберіть місто');
      return;
    }

    setLoading(true);
    setError('');
    setData([]);

    try {
      const url = `/statistics/external_rewards_stats?dateFrom=${dateFrom}&dateTo=${dateTo}&cityShort=${selectedCityShort}`;
      
      const response = await axios.get(url, {
        responseType: 'arraybuffer'
      });

      const workbook = XLSX.read(response.data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet) as BonusStats[];

      const processedData = jsonData.map(item => ({
        ...item,
        conversion: item.uniqueViews > 0 
          ? `${((item.activations / item.uniqueViews) * 100).toFixed(1)}%` 
          : '—'
      }));

      setData(processedData);

      if (processedData.length === 0) {
        setError('Немає інформації за вибраний період');
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Немає доступу до статистики');
      } else {
        setError(err.response?.data?.message || 'Помилка отримання даних');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const url = `/statistics/external_rewards_stats?dateFrom=${dateFrom}&dateTo=${dateTo}&cityShort=${selectedCityShort}`;
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `bonus_analytics_${selectedCityShort}_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err: any) {
      setError('Помилка завантаження Excel файлу');
    }
  };

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3
          }}
        >
          📊 Аналіз бонусів партнерів
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel>Місто</InputLabel>
              <Select
                value={selectedCityShort}
                onChange={(e) => setSelectedCityShort(e.target.value)}
                label="Місто"
              >
                {availableCities.map((city) => (
                  <MenuItem key={city._id} value={getCityShort(city._id)}>
                    {city.cityName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={loading}>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  height: '56px',
                  padding: '0 14px',
                  fontSize: '16px',
                  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'}`,
                  borderRadius: '4px',
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={loading}>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  height: '56px',
                  padding: '0 14px',
                  fontSize: '16px',
                  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'}`,
                  borderRadius: '4px',
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Assessment />}
              onClick={handleFetchData}
              disabled={loading}
              sx={{
                height: '56px',
                backgroundColor: '#007AFF',
                '&:hover': { backgroundColor: '#0051D5' }
              }}
            >
              Отримати аналітику
            </Button>
          </Grid>
        </Grid>

        {data.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadExcel}
            sx={{ borderColor: '#878787' }}
          >
            📥 Завантажити Excel
          </Button>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data.length > 0 && (
        <>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              📋 Статистика використання
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>🎁 Назва бонусу</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>👁️ Переглядів</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>🧍 Унікальних</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>✅ Використань</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>📈 Конверсія</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? 'rgba(78, 205, 196, 0.25)' : 'rgba(78, 205, 196, 0.1)'
                        }
                      }}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="center">{row.views}</TableCell>
                      <TableCell align="center">{row.uniqueViews}</TableCell>
                      <TableCell align="center">{row.activations}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        {row.conversion}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  📊 Використання бонусів
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="activations" fill="#007AFF" name="Використання" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  🥧 Розподіл активацій
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="activations"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={(entry) => `${entry.name}: ${entry.activations}`}
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default UsageAnalysisTab;
